/**
 * Expenses — 4-stage approval workflow: Warden (Maker) → Hostel Manager
 * (Checker) → Director (Final Approver) → Finance (Payment).
 *
 * Every stage supports: Approve · Reject · Send Back for Correction ·
 * Edit (Maker only, on correction) · Remarks · Full version history.
 */
import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Plus, Download, Check, X, AlertCircle, Upload, Loader2, Sparkles,
  ImageIcon, CornerUpLeft, Pencil, Send, Wallet, History as HistoryIcon,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { usePendingApprovals } from "@/context/PendingApprovalsContext";
import { downloadXlsx } from "@/lib/export";

/* ==================================================================
 *  Constants
 * =================================================================*/
const CATS = [
  { v: "travel", l: "Travel" },
  { v: "food", l: "Food" },
  { v: "hostel", l: "Hostel" },
  { v: "maintenance", l: "Maintenance" },
  { v: "utility", l: "Utility Bills" },
  { v: "staff", l: "Staff" },
  { v: "uniforms", l: "Uniforms" },
  { v: "stationery", l: "Stationery" },
  { v: "repairs", l: "Repairs" },
  { v: "misc", l: "Miscellaneous" },
];

// v28.1 — Module classification for P&L reporting
const EXPENSE_FOR_OPTIONS = [
  { v: "hostel", l: "Hostel" },
  { v: "mess", l: "Mess" },
  { v: "general", l: "General / Other" },
];
// Auto-suggest expense_for when category changes
const CATEGORY_TO_EXPENSE_FOR = {
  food: "mess",
  hostel: "hostel", maintenance: "hostel", repairs: "hostel",
  utility: "hostel", staff: "hostel", uniforms: "hostel", stationery: "hostel",
  travel: "general", misc: "general",
};

const STATUS_LABEL = {
  draft: "Draft",
  pending_manager: "Awaiting Hostel Manager",
  pending_director: "Awaiting Director",
  pending_finance: "Awaiting Finance",
  paid: "Paid",
  approved: "Approved",
  rejected: "Rejected",
  correction_needed: "Correction Required",
};

const STAGE_ROLE = {
  pending_manager: "hostel_manager",
  pending_director: "director",
  pending_finance: "accountant",
};

const SEND_BACK_REASONS = [
  { v: "wrong_amount", l: "Wrong Amount" },
  { v: "wrong_vendor", l: "Wrong Vendor" },
  { v: "wrong_category", l: "Wrong Expense Category" },
  { v: "missing_invoice", l: "Missing Invoice" },
  { v: "duplicate_request", l: "Duplicate Request" },
  { v: "budget_exceeded", l: "Budget Exceeded" },
  { v: "incorrect_asset_details", l: "Incorrect Asset Details" },
  { v: "other", l: "Other (see remarks)" },
];

const empty = {
  category: "hostel", subcategory: "", amount: "", vendor: "",
  date: new Date().toISOString().slice(0, 10),
  description: "", quantity: "", cost_center: "", branch: "", bill_url: "",
  expense_for: "hostel",  // v28.1 — module classification for P&L
};

async function fileToBase64(file, maxSide = 1600) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("File read failed"));
    reader.onload = () => {
      if (!file.type.startsWith("image/")) {
        return resolve({ dataUrl: reader.result, base64: reader.result.split(",")[1] });
      }
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
        resolve({ dataUrl, base64: dataUrl.split(",")[1] });
      };
      img.onerror = () => reject(new Error("Not a valid image"));
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

/* ==================================================================
 *  Main page
 * =================================================================*/
const VENDOR_REQUIRED_MSG = "Please select a vendor before creating the expense.";
const ACTION_LABEL = {
  submit: "Submitted", approve: "Approved", reject: "Rejected", send_back: "Sent back",
  payment: "Paid", edit: "Edited", resubmit: "Resubmitted", reverse: "Reversed",
};
const MyLastAction = ({ exp, uid }) => {
  const mine = (exp.approval_history || []).filter((h) => h.by_id === uid);
  const last = mine[mine.length - 1];
  if (!last) return null;
  return (
    <div className="text-[10px] text-muted-foreground mt-1" data-testid={`my-last-action-${exp.id}`}>
      You: {ACTION_LABEL[last.action] || last.action}
      {last.at ? ` · ${new Date(last.at).toLocaleDateString()}` : ""}
    </div>
  );
};

export default function Expenses() {
  const { user } = useAuth();
  // v27.3 — refresh the sidebar pending badge after every expense mutation
  const { refreshExpensesPending } = usePendingApprovals();
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [detail, setDetail] = useState(null);
  // v27.2 — Default tab for approvers = their own Pending stage (never "all").
  // Wardens (makers) still default to "all" since they don't approve anything.
  const DEFAULT_TAB_BY_ROLE = {
    admin: "pending", hostel_manager: "pending",
    director: "pending", accountant: "pending",
    warden: "all", resident: "all",
  };
  const ROLE_HIDDEN_TABS = {
    resident: ["pending", "my_history", "draft", "correction"],
  };
  const [filter, setFilter] = useState(() =>
    DEFAULT_TAB_BY_ROLE[user?.role] || "all"
  );
  const [billPreview, setBillPreview] = useState(null);
  const [ocrLoading, setOcrLoading] = useState(false);
  // v27 — server-side pagination + role-scoped Pending filter
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [q, setQ] = useState("");
  const [listLoading, setListLoading] = useState(false);
  const [pageSum, setPageSum] = useState(0);
  const [ocrDone, setOcrDone] = useState(false);
  const fileRef = useRef(null);

  // v27.2 — Keep the default filter in sync once the AuthContext resolves the
  // logged-in role (user starts as null → then hydrates with role).
  useEffect(() => {
    if (user?.role && filter === "all" && DEFAULT_TAB_BY_ROLE[user.role] === "pending") {
      setFilter("pending");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  // Action dialog
  const [remarks, setRemarks] = useState("");
  const [sendBackOpen, setSendBackOpen] = useState(false);
  const [sendBackReason, setSendBackReason] = useState("");
  const [sendBackRemarks, setSendBackRemarks] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(empty);
  const [editBillPreview, setEditBillPreview] = useState(null);
  const editFileRef = useRef(null);
  const [payOpen, setPayOpen] = useState(false);
  const [payForm, setPayForm] = useState({ utr: "", mode: "bank_transfer", reference: "", remarks: "", payment_proof_url: "" });

  // v27 — server-side pagination fetch; the "Pending" tab is role-scoped so a
  // Director's own approved item disappears the moment status flips to
  // pending_finance (see /expenses/paginated?pending_for_role=…).
  // v27.1 — new "My History" tab uses touched_by_role= so approvers see
  // every expense they acted on that has moved past their stage (or is now
  // Paid). This delivers: Director approves ₹100 → gone from Pending →
  // Finance pays → re-appears here with status Paid.
  const isApprover = ["hostel_manager", "director", "accountant", "admin"].includes(user?.role);
  const showDraftTabs = user?.role !== "resident";
  const loadSeq = useRef(0);
  const PENDING_STAGE = { hostel_manager: "pending_manager", director: "pending_director", accountant: "pending_finance" };
  const isPendingForMe = (e) => {
    if (user?.role === "admin") return Object.values(PENDING_STAGE).includes(e.status);
    if (user?.role === "warden") return e.status === "correction_needed";
    return e.status === PENDING_STAGE[user?.role];
  };
  const load = async () => {
    // v44 — Don't fire until auth has hydrated. Without this guard the page
    // first fetches the unscoped "all" list (slow full-table count on prod),
    // then fires AGAIN once the role flips the default tab to "pending".
    if (!user?.role) return;
    if ((ROLE_HIDDEN_TABS[user.role] || []).includes(filter)) { setFilter("all"); return; }
    setListLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(limit) });
      if (q) params.set("q", q);
      // v46 — tab scope is enforced server-side per role.
      params.set("tab", filter);
      const reqId = ++loadSeq.current;
      const { data } = await api.get(`/expenses/paginated?${params}`);
      if (reqId !== loadSeq.current) return;
      let items = data.items || [];
      if (filter === "pending") items = items.filter(isPendingForMe);
      setList(items);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
      setPageSum(items.reduce((s, e) => s + Number(e.amount || 0), 0));
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setListLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, limit, filter, q, user?.role]);
  useEffect(() => { setPage(1); }, [filter, q]);

  /* ---- OCR-driven bill upload for CREATE dialog ---- */
  const handleFile = async (file, target = "create") => {
    if (!file) return;
    try {
      const { dataUrl, base64 } = await fileToBase64(file);
      if (target === "edit") {
        setEditBillPreview(dataUrl);
        setEditForm((f) => ({ ...f, bill_url: dataUrl }));
        return;
      }
      setBillPreview(dataUrl);
      setForm((f) => ({ ...f, bill_url: dataUrl }));
      setOcrDone(false);
      if (file.type.startsWith("image/")) {
        setOcrLoading(true);
        try {
          const { data } = await api.post("/expenses/ocr", { image_base64: base64 });
          setForm((f) => ({
            ...f,
            amount: data.amount != null ? String(data.amount) : f.amount,
            vendor: data.vendor || f.vendor,
            date: data.date || f.date,
            category: data.category || f.category,
            description: data.description || f.description,
            bill_url: dataUrl,
          }));
          setOcrDone(true);
          toast.success("Bill scanned — fields prefilled");
        } catch (err) { toast.error("OCR failed: " + formatApiError(err)); }
        finally { setOcrLoading(false); }
      }
    } catch (e) { toast.error(e.message || "Could not read file"); }
  };

  const clearBill = () => { setBillPreview(null); setForm((f) => ({ ...f, bill_url: "" })); setOcrDone(false); if (fileRef.current) fileRef.current.value = ""; };

  const submit = async (e, asDraft = false) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!(form.vendor || "").trim()) { toast.error(VENDOR_REQUIRED_MSG); return; }
    try {
      const payload = { ...form, amount: parseFloat(form.amount),
                        quantity: form.quantity ? parseInt(form.quantity) : null };
      await api.post(asDraft ? "/expenses/draft" : "/expenses", payload);
      toast.success(asDraft ? "Saved as draft" : "Expense submitted for approval");
      setOpen(false); setForm(empty); setBillPreview(null); setOcrDone(false);
      load();
      refreshExpensesPending();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const doSubmitDraft = async (id) => {
    try {
      await api.post(`/expenses/${id}/submit`);
      toast.success("Submitted for approval");
      load();
      refreshExpensesPending();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const doDelete = async (exp) => {
    if (!window.confirm(
      `Delete this expense?\n\nDate: ${exp.date}\nCategory: ${exp.category}\nAmount: ₹${exp.amount}\nVendor: ${exp.vendor || "—"}\nCreated by: ${exp.created_by_name || "—"}\n\nThis will archive it — record stays in the audit trail.`
    )) return;
    try {
      await api.delete(`/expenses/${exp.id}`);
      toast.success("Expense archived");
      setDetail(null);
      load();
      refreshExpensesPending();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const [reverseOpen, setReverseOpen] = useState(false);
  const [reverseForm, setReverseForm] = useState({ reason: "", remarks: "" });
  const doReverse = async () => {
    if (!reverseForm.reason.trim()) return toast.error("Reason is mandatory for reversal");
    try {
      await api.post(`/expenses/${detail.id}/reverse`, reverseForm);
      toast.success("Reversal recorded");
      setReverseOpen(false); setReverseForm({ reason: "", remarks: "" });
      await refreshDetail(detail.id);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  /* ---- Actions ---- */
  const canActOn = (exp) => {
    if (!user) return false;
    if (user.role === "admin") return exp.status in STAGE_ROLE;
    return STAGE_ROLE[exp.status] === user.role;
  };
  const isMaker = (exp) => exp && (exp.created_by === user?.id || user?.role === "admin" || user?.role === "hostel_manager");
  const canEdit = (exp) => isMaker(exp) && ["correction_needed", "draft", "pending_manager"].includes(exp.status);
  const canResubmit = (exp) => isMaker(exp) && exp.status === "correction_needed";
  const canSubmitDraft = (exp) => isMaker(exp) && exp.status === "draft";
  const canDelete = (exp) => {
    if (!exp) return false;
    if (exp.status === "paid") return false;
    if (user?.role === "admin") return true;
    return isMaker(exp) && ["draft", "correction_needed", "rejected"].includes(exp.status);
  };
  const canReverse = (exp) => (["admin", "accountant"].includes(user?.role)) && exp.status === "paid" && !exp.reversed;
  const canPay     = (exp) => (user?.role === "accountant" || user?.role === "admin") && exp.status === "pending_finance";

  const refreshDetail = async (id) => {
    try {
      // v27 — reload the list AND fetch the full single-row detail (with bill_url).
      await load();
      const { data } = await api.get(`/expenses/${id}`);
      setDetail(data || null);
      // v27.3 — every mutation that goes through refreshDetail also updates the
      // pending-action badge in the sidebar.
      refreshExpensesPending();
    } catch (err) { console.error("refreshDetail failed", err); }
  };

  // v27 — Open a row: fetch the full detail (with bill_url) via single-get.
  const openDetail = async (row) => {
    setDetail(row);   // show immediately using cached row
    try {
      const { data } = await api.get(`/expenses/${row.id}`);
      setDetail(data);
    } catch (err) { /* keep cached row on failure */ }
  };

  const doApprove = async () => {
    try { await api.post(`/expenses/${detail.id}/approve`, { remarks });
      toast.success("Approved"); setRemarks(""); await refreshDetail(detail.id);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const doReject = async () => {
    try { await api.post(`/expenses/${detail.id}/reject`, { remarks });
      toast.success("Rejected"); setRemarks(""); await refreshDetail(detail.id);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const doSendBack = async () => {
    if (!sendBackReason) { toast.error("Please pick a reason"); return; }
    if (!sendBackRemarks.trim()) { toast.error("Remarks are mandatory"); return; }
    try {
      await api.post(`/expenses/${detail.id}/send-back`, {
        reason: sendBackReason, remarks: sendBackRemarks,
      });
      toast.success("Sent back for correction");
      setSendBackOpen(false); setSendBackReason(""); setSendBackRemarks("");
      await refreshDetail(detail.id);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const doResubmit = async () => {
    try {
      await api.post(`/expenses/${detail.id}/resubmit`, { remarks });
      toast.success("Resubmitted for approval"); setRemarks("");
      await refreshDetail(detail.id);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const doPay = async () => {
    // v31 — Payment proof screenshot is mandatory when Finance marks paid.
    if (!payForm.payment_proof_url) {
      toast.error("Payment proof / screenshot is required to mark as Paid");
      return;
    }
    try {
      await api.post(`/expenses/${detail.id}/payment`, payForm);
      toast.success("Payment recorded");
      setPayOpen(false); setPayForm({ utr: "", mode: "bank_transfer", reference: "", remarks: "", payment_proof_url: "" });
      await refreshDetail(detail.id);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const openEdit = () => {
    setEditForm({
      category: detail.category, subcategory: detail.subcategory || "",
      amount: String(detail.amount ?? ""), vendor: detail.vendor || "",
      date: detail.date, description: detail.description || "",
      quantity: detail.quantity != null ? String(detail.quantity) : "",
      cost_center: detail.cost_center || "", branch: detail.branch || "",
      bill_url: detail.bill_url || "",
    });
    setEditBillPreview(detail.bill_url || null);
    setEditOpen(true);
  };
  const submitEdit = async (e) => {
    e.preventDefault();
    if (!(editForm.vendor || "").trim()) { toast.error(VENDOR_REQUIRED_MSG); return; }
    try {
      const payload = { ...editForm,
        amount: parseFloat(editForm.amount),
        quantity: editForm.quantity ? parseInt(editForm.quantity) : null };
      await api.put(`/expenses/${detail.id}`, payload);
      toast.success("Saved (new version created)");
      setEditOpen(false);
      await refreshDetail(detail.id);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  /* ---- Derived (server already filters + paginates) ---- */
  const filtered = list;

  const totals = { count: total, sum: pageSum };

  const badgeVariant = (s) =>
    s === "paid" || s === "approved" ? "default"
      : s === "rejected" ? "destructive"
      : s === "correction_needed" ? "destructive"
      : "secondary";

  const isDataUrl = (u) => typeof u === "string" && u.startsWith("data:");

  return (
    <div className="space-y-6" data-testid="expenses-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow text-muted-foreground">Finance</div>
          <h1 className="font-display text-4xl font-bold mt-1">Expenses</h1>
          <p className="text-muted-foreground mt-1">
            4-stage approval: Warden → Hostel Manager → Director → Finance. Every step supports
            send-back for correction with full version history.
          </p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <Button variant="outline"
                  onClick={() => downloadXlsx("expenses").catch(() => toast.error("Export failed"))}
                  data-testid="export-expenses">
            <Download className="w-4 h-4 mr-2" /> Excel
          </Button>
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(empty); setBillPreview(null); setOcrDone(false); } }}>
            <DialogTrigger asChild>
              <Button data-testid="add-expense-button"><Plus className="w-4 h-4 mr-2" /> New Expense</Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-display text-2xl">Log Expense</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Bill / Receipt</Label>
                  {billPreview ? (
                    <div className="relative border border-border rounded-md overflow-hidden bg-secondary/30" data-testid="bill-preview">
                      {billPreview.startsWith("data:image") ? (
                        <img src={billPreview} alt="Bill" className="max-h-64 mx-auto" />
                      ) : (
                        <div className="p-6 flex items-center gap-3">
                          <ImageIcon className="w-6 h-6 text-muted-foreground" />
                          <span className="text-sm">Bill file attached</span>
                        </div>
                      )}
                      <button type="button" onClick={clearBill}
                        className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white border border-border flex items-center justify-center hover:bg-destructive hover:text-destructive-foreground"
                        data-testid="clear-bill">
                        <X className="w-4 h-4" />
                      </button>
                      {ocrLoading && (
                        <div className="absolute inset-0 bg-primary/70 flex items-center justify-center text-primary-foreground">
                          <div className="text-center">
                            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                            <div className="text-sm">Scanning bill…</div>
                          </div>
                        </div>
                      )}
                      {ocrDone && !ocrLoading && (
                        <div className="absolute bottom-2 left-2 bg-primary text-primary-foreground text-xs px-2 py-1 rounded-full flex items-center gap-1" data-testid="ocr-done-badge">
                          <Sparkles className="w-3 h-3" /> AI prefilled
                        </div>
                      )}
                    </div>
                  ) : (
                    <div
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) handleFile(f); }}
                      onClick={() => fileRef.current?.click()}
                      data-testid="bill-dropzone"
                      className="border-2 border-dashed border-border rounded-md p-8 text-center cursor-pointer hover:border-primary hover:bg-secondary/40 transition-colors"
                    >
                      <Upload className="w-6 h-6 text-muted-foreground mx-auto mb-2" />
                      <div className="text-sm font-medium">Drop bill photo or click to upload</div>
                      <div className="text-xs text-muted-foreground">AI auto-fills amount, vendor, date, category</div>
                      <input ref={fileRef} type="file" accept="image/*,.pdf" className="hidden"
                        onChange={(e) => handleFile(e.target.files?.[0])} />
                    </div>
                  )}
                </div>

                <ExpenseFormFields form={form} setForm={setForm} testPrefix="exp" />

                <DialogFooter>
                  <Button type="button" variant="outline"
                    onClick={(ev) => submit(ev, true)}
                    data-testid="expense-save-draft">Save as Draft</Button>
                  <Button type="submit" data-testid="expense-save">Submit for Approval</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList data-testid="expense-filter-tabs">
          <TabsTrigger value="all" data-testid="tab-all">All Expenses</TabsTrigger>
          {showDraftTabs && <TabsTrigger value="draft" data-testid="tab-draft">Draft</TabsTrigger>}
          {user?.role !== "resident" && <TabsTrigger value="pending" data-testid="tab-pending">
            {user?.role === "admin" ? "Pending (All Stages)" :
             user?.role === "director" ? "Pending My Approval" :
             user?.role === "hostel_manager" ? "Pending My Approval" :
             user?.role === "accountant" ? "Pending Payment" :
             user?.role === "warden" ? "Sent Back to Me" : "Pending"}
          </TabsTrigger>}
          {showDraftTabs && (
            <TabsTrigger value="my_history" data-testid="tab-my-history"
              title="Expenses you created or acted on, in every stage">
              My History
            </TabsTrigger>
          )}
          {showDraftTabs && <TabsTrigger value="correction" data-testid="tab-correction">Correction</TabsTrigger>}
          <TabsTrigger value="paid" data-testid="tab-paid">Paid</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="tab-rejected">Rejected</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* v27.2 — Contextual banner so the active tab's scope is unmistakable.
          Prevents users from confusing "All Expenses" with "Pending My Approval". */}
      {filter === "pending" && user?.role && user.role !== "admin" && (
        <div className="rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-xs text-primary flex items-center gap-2"
          data-testid="pending-scope-banner">
          <Check className="w-3.5 h-3.5" />
          Only expenses <b>awaiting your action</b> as{" "}
          <b className="capitalize">{user.role.replace("_", " ")}</b> are shown here.
          Once you approve, they disappear from this list and move to the next stage.
        </div>
      )}
      {filter === "all" && user?.role !== "admin" && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 flex items-center gap-2"
          data-testid="all-scope-banner">
          <AlertCircle className="w-3.5 h-3.5" />
          <span>
            <b>All Expenses</b> shows only <b>Paid</b> expenses that you created, approved or paid.
            Items awaiting your action are under the <b>Pending</b> tab.
          </span>
        </div>
      )}
      {filter === "my_history" && (
        <div className="rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs text-emerald-900 flex items-center gap-2"
          data-testid="history-scope-banner">
          <Check className="w-3.5 h-3.5" />
          Expenses you <b>created</b> or <b>acted on</b> — pending, approved, paid, rejected and sent back — with their current stage and your last action.
        </div>
      )}

      {/* Search */}
      <div className="flex items-center gap-2">
        <Input placeholder="Search vendor / description / bill…" value={q}
          onChange={(e) => setQ(e.target.value)} data-testid="expense-search"
          className="max-w-md" />
        {listLoading && <span className="text-xs text-muted-foreground">Loading…</span>}
      </div>

      <Card className="p-0 border-border bg-white overflow-hidden">
        <div className="px-4 py-3 border-b border-border flex items-center gap-3 text-xs text-muted-foreground">
          <span data-testid="expense-count">{totals.count} record(s)</span>
          <span>·</span>
          <span data-testid="expense-total">₹{totals.sum.toLocaleString("en-IN")}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-muted-foreground border-b border-border">
                <th className="py-3 px-4">Date</th>
                <th className="py-3 pr-4">Category</th>
                <th className="py-3 pr-4">Description</th>
                <th className="py-3 pr-4">Vendor</th>
                <th className="py-3 pr-4">Bill</th>
                <th className="py-3 pr-4 text-right">Amount</th>
                <th className="py-3 pr-4">Status</th>
                <th className="py-3 pr-4"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((e) => (
                <tr key={e.id} className="border-b border-border/50 hover:bg-secondary/40">
                  <td className="py-3 px-4 font-mono text-xs">{e.date}</td>
                  <td className="py-3 pr-4 capitalize">{e.category}</td>
                  <td className="py-3 pr-4 max-w-xs truncate">{e.description}</td>
                  <td className="py-3 pr-4 text-muted-foreground">{e.vendor || "—"}</td>
                  <td className="py-3 pr-4">{e.has_bill || e.bill_url ? <ImageIcon className="w-4 h-4 text-primary" /> : "—"}</td>
                  <td className="py-3 pr-4 text-right font-mono">₹{Number(e.amount).toLocaleString("en-IN")}</td>
                  <td className="py-3 pr-4">
                    <Badge variant={badgeVariant(e.status)} className="text-xs">
                      {STATUS_LABEL[e.status] || e.status}
                    </Badge>
                    {e.version > 1 && (
                      <span className="ml-2 text-[10px] text-muted-foreground">v{e.version}</span>
                    )}
                    {filter === "my_history" && <MyLastAction exp={e} uid={user?.id} />}
                  </td>
                  <td className="py-3 pr-4 text-right space-x-1">
                    {canSubmitDraft(e) && (
                      <Button size="sm" variant="secondary" onClick={() => doSubmitDraft(e.id)}
                        data-testid={`submit-draft-${e.id}`}>
                        <Send className="w-3.5 h-3.5 mr-1" /> Submit
                      </Button>
                    )}
                    {canDelete(e) && (
                      <Button size="sm" variant="ghost" onClick={() => doDelete(e)}
                        data-testid={`delete-expense-${e.id}`}
                        title="Delete (soft-delete)">
                        <X className="w-3.5 h-3.5 text-destructive" />
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => openDetail(e)}
                      data-testid={`open-expense-${e.id}`}>Open</Button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={8} className="py-10 text-center text-muted-foreground" data-testid="expenses-empty-state">
                  {filter === "pending" ? "No expenses pending your approval." : "No expenses."}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
        {/* v27 — Server-side pagination footer */}
        <div className="px-4 py-3 border-t border-border flex items-center justify-between text-xs" data-testid="expense-pagination">
          <div className="text-muted-foreground">
            Page {page} of {totalPages} · {total} total
          </div>
          <div className="flex items-center gap-2">
            <Select value={String(limit)} onValueChange={(v) => { setLimit(Number(v)); setPage(1); }}>
              <SelectTrigger className="h-8 w-24" data-testid="expense-limit"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[10, 25, 50, 100].map((n) => <SelectItem key={n} value={String(n)}>{n} / page</SelectItem>)}
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              data-testid="expense-prev">Prev</Button>
            <Button size="sm" variant="outline" disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              data-testid="expense-next">Next</Button>
          </div>
        </div>
      </Card>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl capitalize flex items-center gap-3">
                  {detail.category} — ₹{Number(detail.amount).toLocaleString("en-IN")}
                  <Badge variant={badgeVariant(detail.status)}>{STATUS_LABEL[detail.status] || detail.status}</Badge>
                  {detail.version > 1 && (
                    <Badge variant="outline" className="text-[10px]">v{detail.version}</Badge>
                  )}
                </DialogTitle>
                {detail.status === "correction_needed" && detail.correction_reason && (
                  <DialogDescription className="text-destructive">
                    <AlertCircle className="w-3.5 h-3.5 inline mr-1" />
                    Correction requested — reason: <b>{SEND_BACK_REASONS.find((r)=>r.v===detail.correction_reason)?.l || detail.correction_reason}</b>
                  </DialogDescription>
                )}
              </DialogHeader>

              <Tabs defaultValue="detail" className="mt-2">
                <TabsList>
                  <TabsTrigger value="detail" data-testid="tab-detail">Details</TabsTrigger>
                  <TabsTrigger value="history" data-testid="tab-history">
                    <HistoryIcon className="w-3.5 h-3.5 mr-1.5" /> History ({(detail.approval_history || []).length})
                  </TabsTrigger>
                  <TabsTrigger value="versions" data-testid="tab-versions">
                    Versions ({(detail.versions || []).length})
                  </TabsTrigger>
                </TabsList>

                {/* --- DETAIL TAB --- */}
                <TabsContent value="detail" className="space-y-4 pt-2">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <Info label="Date" value={detail.date} />
                    <Info label="Vendor" value={detail.vendor || "—"} />
                    <Info label="Quantity" value={detail.quantity ?? "—"} />
                    <Info label="Cost Center" value={detail.cost_center || "—"} />
                    <Info label="Branch" value={detail.branch || "—"} />
                    <Info label="Created By" value={`${detail.created_by_name} (${new Date(detail.created_at).toLocaleDateString()})`} />
                  </div>
                  <div>
                    <div className="eyebrow text-muted-foreground mb-1">Description</div>
                    <div className="text-sm">{detail.description}</div>
                  </div>
                  {detail.bill_url && (
                    <div>
                      <div className="eyebrow text-muted-foreground mb-1">Bill</div>
                      {isDataUrl(detail.bill_url) && detail.bill_url.startsWith("data:image") ? (
                        <img src={detail.bill_url} alt="Bill" className="max-h-96 rounded-md border border-border" />
                      ) : isDataUrl(detail.bill_url) ? (
                        <a href={detail.bill_url} download="bill.pdf" className="text-sm text-primary underline">Download bill file →</a>
                      ) : (
                        <a href={detail.bill_url} target="_blank" rel="noreferrer" className="text-sm text-primary underline">Open bill →</a>
                      )}
                    </div>
                  )}
                  {detail.payment && (
                    <Card className="p-3 border-primary/40 bg-primary/5">
                      <div className="text-xs font-semibold uppercase text-primary mb-2 flex items-center gap-2">
                        <Wallet className="w-4 h-4" /> Payment details
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div><span className="text-muted-foreground">UTR:</span> {detail.payment.utr || "—"}</div>
                        <div><span className="text-muted-foreground">Mode:</span> {detail.payment.mode}</div>
                        <div><span className="text-muted-foreground">Date:</span> {new Date(detail.payment.paid_at).toLocaleString()}</div>
                        <div><span className="text-muted-foreground">Ref:</span> {detail.payment.reference || "—"}</div>
                        <div className="col-span-2"><span className="text-muted-foreground">By:</span> {detail.payment.by_name}</div>
                      </div>
                      {/* v31 — Show payment proof screenshot with view + download */}
                      {detail.payment.payment_proof_url && (
                        <div className="mt-3 pt-3 border-t border-primary/20">
                          <div className="text-xs font-semibold text-primary mb-2">Payment Proof</div>
                          <img src={detail.payment.payment_proof_url} alt="payment proof"
                            className="max-w-full max-h-64 rounded border border-border object-contain"
                            data-testid="pay-proof-image" />
                          <a href={detail.payment.payment_proof_url}
                            download={`payment-proof-${detail.id}.jpg`}
                            className="mt-2 inline-flex items-center gap-1 text-xs text-primary underline"
                            data-testid="pay-proof-download">
                            <Download className="w-3 h-3" /> Download
                          </a>
                        </div>
                      )}
                    </Card>
                  )}
                </TabsContent>

                {/* --- HISTORY TAB --- */}
                <TabsContent value="history" className="space-y-2 pt-2">
                  {(detail.approval_history || []).length === 0 && (
                    <div className="text-xs text-muted-foreground">No actions yet.</div>
                  )}
                  {(detail.approval_history || []).map((h, i) => (
                    <div key={h.at || `history-${i}`} className="text-xs border-l-2 border-primary/40 pl-3 py-1.5" data-testid={`history-entry-${i}`}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <ActionBadge action={h.action} />
                        <span className="text-muted-foreground">{h.step?.replace("pending_", "") || "—"}</span>
                        {h.version && <Badge variant="outline" className="text-[10px]">v{h.version}</Badge>}
                        <span className="text-muted-foreground ml-auto">
                          {new Date(h.at).toLocaleString()}
                        </span>
                      </div>
                      <div className="mt-1">
                        <span className="font-medium">{h.by_name}</span>
                        <span className="text-muted-foreground"> ({h.by_role})</span>
                        {h.reason && (
                          <span className="ml-2 text-destructive">
                            · reason: {SEND_BACK_REASONS.find((r)=>r.v===h.reason)?.l || h.reason}
                          </span>
                        )}
                        {h.remarks && <div className="text-muted-foreground mt-0.5">{h.remarks}</div>}
                      </div>
                    </div>
                  ))}
                </TabsContent>

                {/* --- VERSIONS TAB --- */}
                <TabsContent value="versions" className="space-y-2 pt-2">
                  {(detail.versions || []).map((v, i) => (
                    <div key={v.version || `version-${i}`} className="text-xs border border-border rounded-md p-3" data-testid={`version-${v.version}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline">v{v.version}</Badge>
                        <span className="font-medium">{v.by_name}</span>
                        <span className="text-muted-foreground">({v.by_role})</span>
                        <span className="text-muted-foreground ml-auto">{new Date(v.at).toLocaleString()}</span>
                      </div>
                      {v.changes && v.changes.length > 0 ? (
                        <div className="space-y-1">
                          {v.changes.map((c, j) => (
                            <div key={c.field || `change-${j}`} className="grid grid-cols-3 gap-2 font-mono text-[11px]">
                              <div className="text-muted-foreground">{c.field}</div>
                              <div className="text-destructive line-through truncate">{String(c.old ?? "—")}</div>
                              <div className="text-primary truncate">{String(c.new ?? "—")}</div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-muted-foreground italic text-[11px]">Initial submission</div>
                      )}
                    </div>
                  ))}
                </TabsContent>
              </Tabs>

              {/* --- ACTIONS --- */}
              <div className="space-y-3 pt-4 mt-2 border-t border-border">
                {/* Approver actions */}
                {canActOn(detail) && (
                  <div className="space-y-2">
                    <Textarea placeholder="Remarks (optional)" value={remarks} onChange={(e)=>setRemarks(e.target.value)}
                      className="min-h-[70px]" data-testid="approval-remarks" />
                    <div className="flex gap-2 flex-wrap">
                      <Button onClick={doApprove} data-testid="expense-approve">
                        <Check className="w-4 h-4 mr-2" /> Approve
                      </Button>
                      <Button variant="outline" onClick={() => setSendBackOpen(true)} data-testid="expense-send-back">
                        <CornerUpLeft className="w-4 h-4 mr-2" /> Send Back
                      </Button>
                      <Button variant="destructive" onClick={doReject} data-testid="expense-reject">
                        <X className="w-4 h-4 mr-2" /> Reject
                      </Button>
                      {canPay(detail) && (
                        <Button variant="secondary" onClick={() => setPayOpen(true)} data-testid="expense-pay">
                          <Wallet className="w-4 h-4 mr-2" /> Record Payment
                        </Button>
                      )}
                    </div>
                  </div>
                )}

                {/* Maker actions when correction_needed */}
                {canEdit(detail) && (
                  <div className="space-y-2 pt-2 border-t border-dashed border-border">
                    <div className="text-xs text-destructive flex items-start gap-2">
                      <AlertCircle className="w-3.5 h-3.5 mt-0.5" />
                      {detail.status === "draft"
                        ? "This is a draft. Edit or submit when ready."
                        : "This expense was sent back. Edit the required fields and resubmit — workflow restarts from Hostel Manager."}
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <Button onClick={openEdit} data-testid="expense-edit">
                        <Pencil className="w-4 h-4 mr-2" /> Edit
                      </Button>
                      {canSubmitDraft(detail) && (
                        <Button variant="secondary" onClick={() => doSubmitDraft(detail.id)}
                          data-testid="expense-submit-draft-detail">
                          <Send className="w-4 h-4 mr-2" /> Submit for Approval
                        </Button>
                      )}
                      {canResubmit(detail) && (
                        <Button variant="secondary" onClick={doResubmit} data-testid="expense-resubmit">
                          <Send className="w-4 h-4 mr-2" /> Resubmit for Approval
                        </Button>
                      )}
                      {canDelete(detail) && (
                        <Button variant="ghost" onClick={() => doDelete(detail)}
                          data-testid="expense-delete-detail">
                          <X className="w-4 h-4 mr-2 text-destructive" /> Delete
                        </Button>
                      )}
                    </div>
                  </div>
                )}

                {/* Reverse action for paid expenses (admin/accountant) */}
                {canReverse(detail) && (
                  <div className="space-y-2 pt-2 border-t border-dashed border-border">
                    <div className="text-xs text-muted-foreground flex items-start gap-2">
                      <AlertCircle className="w-3.5 h-3.5 mt-0.5" />
                      This expense is paid. Use Reverse to record a controlled reversal / adjustment — the original
                      record stays intact for audit.
                    </div>
                    <Button variant="outline" onClick={() => setReverseOpen(true)}
                      data-testid="expense-reverse">
                      <CornerUpLeft className="w-4 h-4 mr-2" /> Record Reversal
                    </Button>
                  </div>
                )}
                {detail.reversed && (
                  <div className="text-xs text-destructive pt-2 border-t border-dashed border-border">
                    Reversed on {(detail.reversed_at || "").slice(0, 10)} — reversal id {detail.reversal_id?.slice(0, 8)}
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Send-Back dialog */}
      <Dialog open={reverseOpen} onOpenChange={setReverseOpen}>
        <DialogContent data-testid="reverse-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Reverse Paid Expense</DialogTitle>
            <DialogDescription>
              Creates a reversal adjustment record. The original expense stays intact and audit-trailed.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>Reason (mandatory)</Label>
              <Input value={reverseForm.reason} data-testid="reverse-reason"
                onChange={(e) => setReverseForm({ ...reverseForm, reason: e.target.value })}
                placeholder="Wrong amount / duplicate payment / vendor refund / …" />
            </div>
            <div className="space-y-2">
              <Label>Remarks</Label>
              <Textarea rows={3} value={reverseForm.remarks} data-testid="reverse-remarks"
                onChange={(e) => setReverseForm({ ...reverseForm, remarks: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReverseOpen(false)}>Cancel</Button>
            <Button onClick={doReverse} data-testid="reverse-save">Record Reversal</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send-Back dialog */}
      <Dialog open={sendBackOpen} onOpenChange={setSendBackOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Send Back for Correction</DialogTitle>
            <DialogDescription>Pick a reason and add remarks. The maker will be notified and can edit + resubmit.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>Reason *</Label>
              <Select value={sendBackReason} onValueChange={setSendBackReason}>
                <SelectTrigger data-testid="send-back-reason"><SelectValue placeholder="Choose a reason" /></SelectTrigger>
                <SelectContent>
                  {SEND_BACK_REASONS.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Remarks * (mandatory)</Label>
              <Textarea required value={sendBackRemarks} onChange={(e)=>setSendBackRemarks(e.target.value)}
                placeholder="Explain what needs to be corrected…" data-testid="send-back-remarks" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setSendBackOpen(false)}>Cancel</Button>
            <Button onClick={doSendBack} data-testid="send-back-confirm">
              <CornerUpLeft className="w-4 h-4 mr-2" /> Send Back
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Edit Expense</DialogTitle>
            <DialogDescription>Every change creates a new version; nothing is overwritten.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitEdit} className="space-y-4">
            <div className="space-y-2">
              <Label>Bill / Receipt (replace or upload new)</Label>
              {editBillPreview ? (
                <div className="relative border border-border rounded-md overflow-hidden bg-secondary/30">
                  {editBillPreview.startsWith("data:image") ? (
                    <img src={editBillPreview} alt="Bill" className="max-h-48 mx-auto" />
                  ) : (
                    <div className="p-4 text-sm">Bill file attached</div>
                  )}
                  <button type="button"
                    onClick={()=>{setEditBillPreview(null); setEditForm((f)=>({...f, bill_url: ""}));}}
                    className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white border border-border flex items-center justify-center hover:bg-destructive hover:text-destructive-foreground">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div onClick={() => editFileRef.current?.click()}
                  className="border-2 border-dashed border-border rounded-md p-6 text-center cursor-pointer hover:border-primary">
                  <Upload className="w-5 h-5 text-muted-foreground mx-auto mb-1" />
                  <div className="text-sm">Upload new bill</div>
                  <input ref={editFileRef} type="file" accept="image/*,.pdf" className="hidden"
                    onChange={(e)=>handleFile(e.target.files?.[0], "edit")} />
                </div>
              )}
            </div>
            <ExpenseFormFields form={editForm} setForm={setEditForm} testPrefix="edit-exp" />
            <DialogFooter>
              <Button variant="outline" type="button" onClick={()=>setEditOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="edit-expense-save">Save New Version</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Payment dialog */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Record Payment</DialogTitle>
            <DialogDescription>Enter UTR / reference and payment mode to finalize.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>UTR / Transaction Number</Label>
              <Input value={payForm.utr} onChange={(e)=>setPayForm({...payForm, utr:e.target.value})}
                data-testid="pay-utr" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Mode</Label>
                <Select value={payForm.mode} onValueChange={(v)=>setPayForm({...payForm, mode: v})}>
                  <SelectTrigger data-testid="pay-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    <SelectItem value="upi">UPI</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                    <SelectItem value="card">Card</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Reference</Label>
                <Input value={payForm.reference} onChange={(e)=>setPayForm({...payForm, reference:e.target.value})}
                  data-testid="pay-reference" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Remarks</Label>
              <Textarea value={payForm.remarks} onChange={(e)=>setPayForm({...payForm, remarks:e.target.value})}
                data-testid="pay-remarks" />
            </div>
            {/* v31 — Mandatory payment proof screenshot */}
            <div className="space-y-2">
              <Label>Payment Proof / Screenshot <span className="text-destructive">*</span></Label>
              <Input required={!payForm.payment_proof_url} type="file" accept="image/*"
                onChange={async (e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  const rd = new FileReader();
                  rd.onload = () => {
                    const img = new Image();
                    img.onload = () => {
                      const c = document.createElement("canvas");
                      const scale = Math.min(1, 1600 / Math.max(img.width, img.height));
                      c.width = img.width * scale; c.height = img.height * scale;
                      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
                      setPayForm(pf => ({ ...pf, payment_proof_url: c.toDataURL("image/jpeg", 0.82) }));
                    };
                    img.onerror = () => setPayForm(pf => ({ ...pf, payment_proof_url: rd.result }));
                    img.src = rd.result;
                  };
                  rd.readAsDataURL(f);
                }}
                data-testid="pay-proof-file" />
              {payForm.payment_proof_url ? (
                <div className="flex items-center gap-2">
                  <img alt="proof" src={payForm.payment_proof_url} className="w-16 h-16 object-cover rounded" />
                  <span className="text-xs text-emerald-700 font-medium">Screenshot attached</span>
                </div>
              ) : (
                <p className="text-xs text-destructive">Payment proof screenshot is required to mark this expense Paid.</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setPayOpen(false)}>Cancel</Button>
            <Button onClick={doPay} data-testid="pay-confirm">
              <Wallet className="w-4 h-4 mr-2" /> Mark as Paid
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* --------- Sub-components --------- */
function ExpenseFormFields({ form, setForm, testPrefix }) {
  // v28.1 — when category changes, auto-suggest an expense_for module (user can still override)
  const onCategoryChange = (v) => {
    const suggested = CATEGORY_TO_EXPENSE_FOR[v];
    setForm({ ...form, category: v, ...(suggested ? { expense_for: suggested } : {}) });
  };
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div className="space-y-2">
          <Label>Expense For *</Label>
          <Select value={form.expense_for || "hostel"} onValueChange={(v)=>setForm({...form, expense_for:v})}>
            <SelectTrigger data-testid={`${testPrefix}-expense-for`}><SelectValue /></SelectTrigger>
            <SelectContent>{EXPENSE_FOR_OPTIONS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Category *</Label>
          <Select value={form.category} onValueChange={onCategoryChange}>
            <SelectTrigger data-testid={`${testPrefix}-category`}><SelectValue /></SelectTrigger>
            <SelectContent>{CATS.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Subcategory</Label>
          <Input value={form.subcategory} onChange={(e)=>setForm({...form, subcategory:e.target.value})}
            data-testid={`${testPrefix}-subcategory`} />
        </div>
        <div className="space-y-2">
          <Label>Amount *</Label>
          <Input required type="number" step="0.01" value={form.amount}
            onChange={(e)=>setForm({...form, amount:e.target.value})} data-testid={`${testPrefix}-amount`} />
        </div>
        <div className="space-y-2">
          <Label>Vendor <span className="text-destructive">*</span></Label>
          <Input value={form.vendor} onChange={(e)=>setForm({...form, vendor:e.target.value})}
            placeholder="Vendor name (required)" data-testid={`${testPrefix}-vendor`} />
        </div>
        <div className="space-y-2">
          <Label>Date *</Label>
          <Input required type="date" value={form.date}
            onChange={(e)=>setForm({...form, date:e.target.value})} data-testid={`${testPrefix}-date`} />
        </div>
        <div className="space-y-2">
          <Label>Quantity</Label>
          <Input type="number" min={1} value={form.quantity}
            onChange={(e)=>setForm({...form, quantity:e.target.value})} data-testid={`${testPrefix}-quantity`} />
        </div>
        <div className="space-y-2">
          <Label>Cost Center</Label>
          <Input value={form.cost_center} onChange={(e)=>setForm({...form, cost_center:e.target.value})} />
        </div>
        <div className="space-y-2">
          <Label>Branch</Label>
          <Input value={form.branch} onChange={(e)=>setForm({...form, branch:e.target.value})} />
        </div>
      </div>
      <div className="space-y-2">
        <Label>Description *</Label>
        <Textarea required value={form.description}
          onChange={(e)=>setForm({...form, description:e.target.value})}
          data-testid={`${testPrefix}-description`} />
      </div>
    </>
  );
}

function ActionBadge({ action }) {
  const map = {
    submit:  { l: "SUBMIT",  cls: "bg-blue-100 text-blue-800" },
    approve: { l: "APPROVE", cls: "bg-emerald-100 text-emerald-800" },
    reject:  { l: "REJECT",  cls: "bg-rose-100 text-rose-800" },
    send_back: { l: "SEND BACK", cls: "bg-amber-100 text-amber-800" },
    edit:    { l: "EDIT",    cls: "bg-indigo-100 text-indigo-800" },
    resubmit:{ l: "RESUBMIT",cls: "bg-blue-100 text-blue-800" },
    payment: { l: "PAYMENT", cls: "bg-primary/10 text-primary" },
  };
  const m = map[action] || { l: action.toUpperCase(), cls: "bg-secondary" };
  return <span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded ${m.cls}`}>{m.l}</span>;
}

const Info = ({ label, value }) => (
  <div>
    <div className="eyebrow text-muted-foreground mb-1">{label}</div>
    <div className="font-medium">{value}</div>
  </div>
);
